import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import './i18n';
import { registerServiceWorker } from './lib/registerSW';
import { initSentry } from './lib/monitoring/sentry';
import { initErrorTracking } from './lib/monitoring/errorTracker';
import { initPerformanceMonitoring } from './lib/monitoring/performanceBenchmark';
import { installNumberInputAutoSelect } from './lib/utils/numberInputAutoSelect';
import { purgeLegacyMfaTrustKeys } from './lib/queries/auth-security';

// Delete the retired `nizamone_mfa_verified_*` device-trust keys. They are no longer read
// by anything — the MFA decision now comes from the signed token — so this is hygiene, not
// a control: it clears keys an attacker may have planted and stops an auditor mistaking a
// dead value for a live one. Deleted outright; never migrated, never used as a fallback.
purgeLegacyMfaTrustKeys();

// Initialize monitoring before React renders.
// initSentry MUST run first so it can capture any startup errors from
// errorTracker / performance monitor / service-worker registration.
// Sentry is gated on VITE_SENTRY_DSN — no-op when unset (dev, preview).
initSentry();
initErrorTracking();
initPerformanceMonitoring();

// Global UX: any focused <input type="number"> showing "0" auto-selects
// so the next keystroke replaces it instead of appending.
installNumberInputAutoSelect();

registerServiceWorker();

// Auto-recover from failed lazy-chunk loads. After a new deploy replaces hashed asset files
// (or on a transient network failure), a dynamically-imported route chunk can 404/fail and Vite
// fires `vite:preloadError`. Reload once to pull the fresh index.html + current chunks instead of
// crashing the route's ErrorBoundary (e.g. "Failed to fetch dynamically imported module: LeavePage").
// Guarded via sessionStorage so a genuinely-broken chunk can't cause an infinite reload loop.
window.addEventListener('vite:preloadError', (event) => {
  const KEY = 'nz-preload-reload-at';
  const now = Date.now();
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (now - last > 15000) {
    sessionStorage.setItem(KEY, String(now));
    event.preventDefault();
    window.location.reload();
  }
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
